import groupBy from 'lodash.groupby';
import orderBy from 'lodash.orderby';

import { type NotificationEvent, NotificationReasonType, NotificationType } from '../../common/core-api';

export class NotificationHelper {
  private notifications: Array<NotificationEvent>;

  constructor(notifications: Array<NotificationEvent>) {
    this.notifications = notifications;
  }

  private isNewIdeaInChallenge = (group: Array<NotificationEvent>) =>
    group.some(
      (notifiction: NotificationEvent) =>
        notifiction.type === NotificationType.IDEA_NEW &&
        notifiction.reasonType === NotificationReasonType.FOLLOWED_CHALLENGE
    );

  public groupByReasonAndType = (): Array<Array<NotificationEvent>> => {
    // group notification by reason type
    const groupedByReasonType = Object.values(
      groupBy(this.notifications, (notif) =>
        notif.type === NotificationType.CHALLENGE_CHECK_OUT
          ? `${notif.reasonType}${notif.challengeId}`
          : notif.reasonType
      )
    );

    // group notifications by reason id inside each group that is grouped by reasonType
    const groupedByReasonId = groupedByReasonType.flatMap((group: Array<NotificationEvent>) => {
      if (group.length === 0) return [];
      const isBlogComment = group[0].type === NotificationType.BLOG_POST_COMMENT;
      const groupKey = isBlogComment
        ? (n: NotificationEvent) => `${n.reasonId}_${n.userId ?? 'noUser'}`
        : (n: NotificationEvent) => n.reasonId;

      const filterGroups = isBlogComment ? group.filter((n) => n.userId !== null && n.userId !== undefined) : group;

      return Object.values(groupBy(filterGroups, groupKey));
    });

    // group by type notifications that are already grouped arrays by reasonType and reasonId
    return groupedByReasonId.flatMap((group: Array<NotificationEvent>) =>
      Object.values(groupBy(group, (notif) => notif.type))
    );
  };

  public sortByCreatedDateInsideGroups = (groupedNotification: Array<Array<NotificationEvent>>) =>
    groupedNotification.map((group: Array<NotificationEvent>) => orderBy(group, ['created'], ['desc']));

  public sortGroupsByDate = (groupedNotification: Array<Array<NotificationEvent>>) => {
    // create Map where key is first item of group and value is whole group
    const groupedMap = new Map<NotificationEvent, NotificationEvent[]>();
    groupedNotification.forEach((group: Array<NotificationEvent>) => {
      // if notification type is NEW_IDEA and reason is CHALLENGE then most recent item in group should be taken as key
      this.isNewIdeaInChallenge(group)
        ? groupedMap.set(orderBy(group, ['created'], ['desc'])[0], group)
        : groupedMap.set(group[0], group);
    });

    // create array of Map keys
    const keysArr = [];
    for (const key of groupedMap.keys()) {
      keysArr.push(key);
    }

    // sort array of keys by created date
    const sortedKeysArr = orderBy(keysArr, ['created'], ['desc']);

    // get groups of notifications from Map by sorted keys
    return sortedKeysArr.map((key) => groupedMap.get(key));
  };
}

export const groupNotifications = (notifications: NotificationEvent[]) => {
  const notificationHelper = new NotificationHelper(notifications);

  if (notifications.length === 0) {
    return notifications;
  }

  const grouped = notificationHelper.groupByReasonAndType();
  const sortedInsideGroups = notificationHelper.sortByCreatedDateInsideGroups(grouped);
  return notificationHelper.sortGroupsByDate(sortedInsideGroups);
};
